import {
  Content,
  Group,
  Icon,
  Item,
  ItemIndicator,
  ItemText,
  Label,
  Portal,
  Root,
  Trigger,
  Value,
  Viewport,
} from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import type { ComponentPropsWithRef, ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean | undefined;
  /** Decorative glyph shown before the label in the listbox (not in the
   *  trigger; use `triggerIcon` for that). */
  icon?: ReactNode;
}

export interface SelectProps extends Omit<
  ComponentPropsWithRef<typeof Trigger>,
  'onChange' | 'value' | 'defaultValue'
> {
  options: readonly SelectOption[];
  value?: string | undefined;
  defaultValue?: string | undefined;
  onValueChange?: ((value: string) => void) | undefined;
  placeholder?: string | undefined;
  name?: string | undefined;
  /** Radix portals the listbox to document.body, outside any `.pl-on-map`
   *  subtree. Pass `pl-on-map` for a select that belongs to map chrome. */
  portalClassName?: string | undefined;
  /** A non-selectable caption at the top of the listbox. */
  groupLabel?: string | undefined;
  /** Decorative glyph shown in the trigger before the value. */
  triggerIcon?: ReactNode;
}

/**
 * Single-select listbox.
 *
 * Radix rather than a native `<select>`: the workspace needs the options
 * styled to match the rest of the panel chrome, and a native select's option
 * list is drawn by the OS and cannot be. What Radix buys back is the part that
 * usually breaks when people rebuild this — typeahead, Home/End, arrow-key
 * navigation, and the correct listbox/option roles.
 *
 * Accepts `aria-describedby`, `aria-invalid` and `required` straight from
 * `Field`'s render prop, so it wires up the same way an `Input` does.
 */
export function Select({
  options,
  value,
  defaultValue,
  onValueChange,
  placeholder,
  name,
  className,
  portalClassName,
  groupLabel,
  triggerIcon,
  ...triggerProps
}: SelectProps) {
  const items = (
    <>
      {options.map((option) => (
        <Item
          key={option.value}
          value={option.value}
          {...(option.disabled === undefined ? {} : { disabled: option.disabled })}
          className={cn(
            'relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-7',
            'text-sm outline-none select-none',
            'data-[highlighted]:bg-accent-soft data-[highlighted]:text-fg',
            'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
          )}
        >
          <ItemIndicator className="absolute left-2 inline-flex items-center">
            <Check aria-hidden className="size-3.5" />
          </ItemIndicator>
          {option.icon === undefined ? null : (
            <span aria-hidden className="inline-flex shrink-0 text-fg-muted">
              {option.icon}
            </span>
          )}
          <ItemText>{option.label}</ItemText>
        </Item>
      ))}
    </>
  );

  return (
    // Conditional spreads, not `name={name}`. The base tsconfig sets
    // exactOptionalPropertyTypes, and Radix types these as `name?: string`
    // rather than `name?: string | undefined` — so passing an explicit
    // undefined is a type error even though it is what React does anyway.
    <Root
      {...(value === undefined ? {} : { value })}
      {...(defaultValue === undefined ? {} : { defaultValue })}
      {...(onValueChange === undefined ? {} : { onValueChange })}
      {...(name === undefined ? {} : { name })}
    >
      <Trigger
        className={cn(
          'inline-flex h-10 w-full items-center justify-between gap-2 rounded-md',
          'border border-line bg-surface-sunken px-3 text-sm text-fg',
          'transition-colors duration-(--pl-duration-fast)',
          'hover:border-line-strong',
          'data-[placeholder]:text-fg-subtle',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'aria-[invalid=true]:border-danger',
          className,
        )}
        {...triggerProps}
      >
        {triggerIcon === undefined ? null : (
          <span aria-hidden className="inline-flex shrink-0 text-fg-muted">
            {triggerIcon}
          </span>
        )}
        <span className="min-w-0 flex-1 truncate text-left">
          <Value placeholder={placeholder} />
        </span>
        <Icon asChild>
          <ChevronDown aria-hidden className="size-4 shrink-0 text-fg-muted" />
        </Icon>
      </Trigger>

      <Portal>
        <Content
          position="popper"
          sideOffset={4}
          className={cn(
            portalClassName,
            'z-(--pl-z-overlay) min-w-(--radix-select-trigger-width) overflow-hidden',
            'rounded-md border border-line bg-surface-raised text-fg shadow-overlay',
          )}
        >
          <Viewport className="max-h-64 p-1">
            {groupLabel === undefined ? (
              items
            ) : (
              <Group>
                <Label className="px-2 py-1 text-xs font-medium text-fg-subtle">{groupLabel}</Label>
                {items}
              </Group>
            )}
          </Viewport>
        </Content>
      </Portal>
    </Root>
  );
}
